/* HUENIT LAB 2.0 — Admin: 코드 예제(블록·Python) 관리.
   S4 = S3(official/*) 실연동. 목록은 official manifest 에서 로드, 등록/수정/삭제는
   객체 업로드 + meta.json + manifest RMW(repo.js). 분류(cats/filters)·언어(langs)는
   CMS 전용 소스데이터라 localStorage 유지(plan §7.2).
   블록/Python 은 manifest 가 분리돼 있고(repo.js 상단) 화면도 종류 탭으로 나눠 본다 —
   그림·툴패스 화면(examples-engrave)과 같은 구조. 앱은 두 manifest 를 모두 읽어
   블록/Python 탭에 각각 노출한다(handoff/APP-python-examples.md · BE-0008).
   fake 모드(env 미설정)도 동일 코드로 동작(s3.js fake). */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, MLText, Seg, useToast, Loading, ErrorState, EmptyState } from '../../components/index.js';
import {
  useLocal,
  LANG_SEED, SEED_EX_CATS, SEED_EX_FILTERS, SEED_EXAMPLES,
} from '../../data/store.js';
import { mlGet } from '../../lib/ml.js';
import { useSession } from '../../auth/session.jsx';
import {
  listExamples, saveExample, deleteExample, newAssetId, exampleFileUrl,
  isLegacyPythonAsset, republishLegacyPython,
} from './repo.js';
import { downloadObject } from '../../lib/download.js';

const KIND_LABEL = { block: '블록 코딩', python: 'Python' };
const KIND_ICON = { block: 'blocks', python: 'python' };

const SEED_FLAG = 'adm.fake.examplesSeeded';
let seedInFlight = null;   // StrictMode 이중 마운트의 동시 시드 RMW 경쟁 방지(모듈 단위 가드)

/* ============ 파일 드롭존 ============ */
function FileDrop({ kind, fileName, onPick, onClear }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const accept = kind === 'python' ? '.py' : '.huenit';
  // 드래그&드롭은 input 의 accept 필터를 우회하므로 확장자를 직접 검증(잘못된 파일 업로드 방지).
  const pick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(accept)) { toast(`${accept} 파일만 올릴 수 있습니다.`, 'error'); return; }
    onPick(f);
  };
  return (
    <div>
      {fileName ? (
        <div className="dropzone__file">
          <Icon name={kind === 'python' ? 'python' : 'blocks'} size={18} />
          <span className="dropzone__file-name">{fileName}</span>
          <button type="button" className="icon-btn icon-btn--ghost" onClick={onClear}><Icon name="close" size={16} /></button>
        </div>
      ) : (
        <div className={"dropzone" + (over ? " is-over" : "")}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
          <div className="dropzone__icon"><Icon name="upload" size={22} /></div>
          <div className="dropzone__t">{kind === 'python' ? 'Python 파일(.py)' : '블록 코딩 파일(.huenit)'}을 끌어다 놓기</div>
          <div className="dropzone__d">또는 클릭해서 선택 · 앱에서 export 한 파일</div>
          <input ref={inputRef} type="file" accept={accept} hidden onChange={e => pick(e.target.files[0])} />
        </div>
      )}
    </div>
  );
}

/* ============ 썸네일 드롭존(선택) ============ */
/* 그림·툴패스 예제 화면(examples-engrave)도 그대로 재사용한다 — 계약(썸네일 = 절대
   HTTPS URL, extras.thumbnail)이 동일하므로 중복 구현하지 않는다. */
export function ThumbDrop({ url, file, onPick, onRemove }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const objUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  useEffect(() => () => { if (objUrl) URL.revokeObjectURL(objUrl); }, [objUrl]);
  const preview = objUrl || url || '';
  const pick = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type) && !/\.(png|jpe?g|webp|gif)$/i.test(f.name)) { toast('이미지 파일만 올릴 수 있습니다.', 'error'); return; }
    onPick(f);
  };
  if (preview) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src={preview} alt="썸네일 미리보기" style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--ink-200)' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => inputRef.current && inputRef.current.click()}>교체</button>
          <button type="button" className="btn btn--danger btn--sm" onClick={onRemove}>제거</button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => pick(e.target.files[0])} />
      </div>
    );
  }
  return (
    <div className={"dropzone" + (over ? " is-over" : "")} onClick={() => inputRef.current && inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
      <div className="dropzone__icon"><Icon name="upload" size={20} /></div>
      <div className="dropzone__t">썸네일 이미지 (선택)</div>
      <div className="dropzone__d">PNG·JPG·WEBP · 앱 예제 카드에 표시(앱 지원 후)</div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => pick(e.target.files[0])} />
    </div>
  );
}

/* ============ 예제 추가/수정 모달 ============ */
function ExampleModal({ draft, setDraft, cats, filters, langs, onSave, onClose, isNew, saving, step }) {
  const d = draft;
  const set = (patch) => setDraft({ ...d, ...patch });
  const toggleFilter = (id) => set({ filterIds: d.filterIds.includes(id) ? d.filterIds.filter(x => x !== id) : [...d.filterIds, id] });
  const def = (langs.find(l => l.default) || langs[0]).code;
  const hasFile = isNew ? !!d.fileObj : (!!d.fileObj || !!d.file);
  const canSave = !saving && (d.title[def] || '').trim() && d.catId && hasFile;
  return (
    <Modal wide title={isNew ? '예제 추가' : '예제 수정'} sub="제목·내용은 언어별로 입력할 수 있어요." onClose={saving ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={onSave}>
          {saving ? (step || '저장 중…') : (isNew ? '예제 등록' : '저장')}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">코드 종류</label>
        <Seg value={d.kind} onChange={v => { if (isNew) set({ kind: v, file: '', fileObj: null }); }}
             options={[{ value: 'block', label: '블록 코딩', icon: 'blocks' }, { value: 'python', label: 'Python', icon: 'python' }]} />
        {!isNew && <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>수정 시 코드 종류는 변경할 수 없습니다.</div>}
      </div>
      <div className="field">
        <label className="field__label">코드 파일<span className="req">*</span></label>
        <FileDrop kind={d.kind} fileName={d.file}
          onPick={f => set({ fileObj: f, file: f.name })}
          onClear={() => set({ fileObj: null, file: '' })} />
      </div>
      <div className="field">
        <label className="field__label">썸네일 <span style={{ fontWeight: 500, color: 'var(--ink-400)' }}>(선택)</span></label>
        <ThumbDrop url={d.thumbnail} file={d.thumbFile}
          onPick={f => set({ thumbFile: f, thumbRemoved: false })}
          onRemove={() => set({ thumbFile: null, thumbnail: '', thumbRemoved: true })} />
      </div>
      <div className="field">
        <label className="field__label">제목<span className="req">*</span></label>
        <MLText value={d.title} onChange={v => set({ title: v })} langs={langs} placeholder="예제 제목" />
      </div>
      <div className="field">
        <label className="field__label">내용 설명</label>
        <MLText value={d.desc} onChange={v => set({ desc: v })} langs={langs} multiline placeholder="예제에 대한 설명" />
      </div>
      <div className="field-grid">
        <div className="field">
          <label className="field__label">카테고리<span className="req">*</span></label>
          <select className="select-el" value={d.catId} onChange={e => set({ catId: e.target.value })}>
            <option value="">선택하세요</option>
            {cats.map(c => <option key={c.id} value={c.id}>{mlGet(c.label, def)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field__label">필터 태그</label>
          <div className="ex-card__tags" style={{ marginTop: 2 }}>
            {filters.map(f => {
              const on = d.filterIds.includes(f.id);
              return <button key={f.id} type="button" className="chip" onClick={() => toggleFilter(f.id)}
                style={{ cursor: 'pointer', background: on ? 'var(--brand-600)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-600)' }}>{mlGet(f.label, def)}</button>;
            })}
            {filters.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink-400)' }}>등록된 필터가 없습니다.</span>}
          </div>
        </div>
      </div>
      <div className="field">
        <label className="field__label">배치 순서 <span style={{ fontWeight: 500, color: 'var(--ink-400)' }}>(선택 · 묶음-번호)</span></label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input className="input mono" style={{ width: 88, textAlign: 'center' }} type="number" min="1" placeholder="묶음"
            value={d.orderGroup} onChange={e => set({ orderGroup: e.target.value })} />
          <span style={{ fontWeight: 800, color: 'var(--ink-400)' }}>-</span>
          <input className="input mono" style={{ width: 88, textAlign: 'center' }} type="number" min="1" placeholder="번호"
            value={d.orderIndex} onChange={e => set({ orderIndex: e.target.value })} />
          {(d.orderGroup !== '' || d.orderIndex !== '') && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => set({ orderGroup: '', orderIndex: '' })}>지우기</button>
          )}
        </div>
        <div className="field__hint">쉬운 것부터 앞으로. 이미 있는 번호를 주면 그 묶음 안에서 뒤로 한 칸씩 밀립니다(다른 묶음은 그대로). 비우면 맨 뒤.</div>
      </div>
    </Modal>
  );
}

/* ============ 코드 예제 관리 ============ */
export function ExamplesAdmin() {
  const toast = useToast();
  const { mode, email, getCredentials } = useSession();
  const [langs] = useLocal('adm.langs', LANG_SEED);
  const [cats] = useLocal('adm.exCats', SEED_EX_CATS);
  const [filters] = useLocal('adm.exFilters', SEED_EX_FILTERS);

  const def = (langs.find(l => l.default) || langs[0]).code;
  const [items, setItems] = useState(null);   // null=로딩, []=빈, [..]=목록
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('block');    // 코드 종류 탭 — 앱 예제 탭의 블록/Python 과 동일 축
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('');
  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [fixing, setFixing] = useState('');   // '' | 진행 문구 (레거시 파이썬 재발행)
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;

  const owner = () => ({ email: email || '', defaultLang: def, cats, filters });

  // fake 모드 1회 시드 — 빈 데모를 피하고 기존 seed 예제로 회귀 없이 동작.
  // seed id 는 고정이라 재시드해도 manifest RMW 가 id 로 덮어쓴다(중복 없음).
  const seedFakeOnce = useCallback(async () => {
    try { if (localStorage.getItem(SEED_FLAG)) return; } catch (e) { return; }
    if (seedInFlight) return seedInFlight;
    seedInFlight = (async () => {
      for (const ex of SEED_EXAMPLES) {
        const buf = new TextEncoder().encode('// ' + (ex.file || 'seed')).buffer;
        await saveExample(getCredentials, { example: ex, fileBuffer: buf, owner: owner() });
      }
      try { localStorage.setItem(SEED_FLAG, '1'); } catch (e) { /* ignore */ }
    })();
    try { await seedInFlight; } finally { seedInFlight = null; }
  }, [getCredentials, email, def, cats, filters]);

  const load = useCallback(async () => {
    setError(null); setItems(null);
    try {
      if (mode === 'fake') await seedFakeOnce();
      setItems(await listExamples());
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
      setItems([]);
    }
  }, [mode, seedFakeOnce]);

  useEffect(() => { load(); }, [load]);

  const blank = (kind) => ({ id: newAssetId(), kind, file: '', fileObj: null, catId: '', filterIds: [], title: {}, desc: {}, thumbnail: '', thumbFile: null, thumbRemoved: false, orderGroup: '', orderIndex: '' });
  const openNew = () => { setDraft(blank(tab)); setEditing('new'); };
  const openEdit = (ex) => { setDraft({ ...JSON.parse(JSON.stringify(ex)), fileObj: null, thumbFile: null, thumbRemoved: false }); setEditing(ex.id); };
  const closeModal = () => { if (!saving) { setEditing(null); setDraft(null); } };

  const save = async () => {
    setSaving(true); setStep('');
    try {
      const fileBuffer = draft.fileObj ? await draft.fileObj.arrayBuffer() : null;
      await saveExample(getCredentials, { example: draft, fileBuffer, thumbnailFile: draft.thumbFile, removeThumbnail: draft.thumbRemoved, owner: owner(), onStep: setStep });
      toast(editing === 'new' ? '예제를 등록했습니다' : '예제를 수정했습니다');
      setTab(draft.kind);                    // 다른 종류로 등록했으면 그 탭으로 따라간다
      setEditing(null); setDraft(null);
      await load();
    } catch (e) {
      toast('저장 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setSaving(false); setStep('');
    }
  };

  const remove = async () => {
    setDelBusy(true);
    try {
      await deleteExample(getCredentials, del);
      toast('예제를 삭제했습니다');
      setDel(null);
      await load();
    } catch (e) {
      toast('삭제 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setDelBusy(false);
    }
  };

  const download = async (ex) => {
    if (mode === 'fake') { toast('데모(오프라인) 모드에서는 실제 파일 다운로드를 지원하지 않습니다.'); return; }
    try {
      await downloadObject(exampleFileUrl(ex), ex.file || (ex.kind === 'python' ? 'main.py' : 'workspace.huenit'));
    } catch (e) {
      toast('다운로드 실패: ' + (e && e.message ? e.message : e), 'error');
    }
  };

  // 레거시 파이썬 자산(kind:'pythonCode') 재발행 — 앱이 그 항목을 파싱 단계에서 버리므로
  // 고치기 전까지 앱 Python 탭에 뜨지 않는다. CMS 목록엔 정상으로 보여서 화면에서 알린다.
  const republish = async () => {
    setFixing('시작 중…');
    try {
      const n = await republishLegacyPython(getCredentials, setFixing);
      toast(n > 0 ? `파이썬 예제 ${n}건을 앱 호환 형식으로 재발행했습니다` : '재발행할 항목이 없습니다');
      await load();
    } catch (e) {
      toast('재발행 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setFixing('');
    }
  };

  const catById = (id) => cats.find(c => c.id === id);
  const list = items || [];
  const count = (k) => list.filter(e => e.kind === k).length;
  const legacyCount = list.filter(e => isLegacyPythonAsset(e._meta)).length;
  const shown = list.filter(e =>
    e.kind === tab &&
    (catFilter === 'all' || e.catId === catFilter) &&
    (q.trim() === '' || mlGet(e.title, def).toLowerCase().includes(q.trim().toLowerCase())));
  // 6개씩 페이지네이션 (shown 은 이미 배치 순서 정렬됨). 검색/필터/삭제로 페이지 넘치면 보정.
  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  useEffect(() => { setPage(1); }, [q, catFilter, tab]);   // 검색·필터·탭 바뀌면 1페이지로
  const pageItems = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Seg value={tab} onChange={setTab} options={[
          { value: 'block', label: `블록 코딩${items ? ` (${count('block')})` : ''}`, icon: 'blocks' },
          { value: 'python', label: `Python${items ? ` (${count('python')})` : ''}`, icon: 'python' },
        ]} />
        <div className="field__hint" style={{ marginTop: 6 }}>
          {tab === 'block'
            ? '앱에서 export 한 블록 코딩 파일(.huenit). 앱 예제 탭의 [블록 코딩]에 노출됩니다.'
            : '앱에서 export 한 Python 파일(.py). 앱 예제 탭의 [Python]에 노출됩니다.'}
        </div>
      </div>

      {legacyCount > 0 && (
        <div className="ex-card" style={{ padding: '12px 14px', marginBottom: 14, background: '#fff4e5', border: '1px solid #f0d3a8' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Icon name="python" size={16} />
            <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: '#8a5209' }}>
              <b>파이썬 예제 {legacyCount}건이 앱에 보이지 않는 옛 형식으로 저장돼 있습니다.</b>
              <div style={{ marginTop: 2, color: '#b4690e' }}>앱이 읽을 수 있는 형식으로 다시 발행하세요. 내용·파일·순서는 그대로입니다.</div>
            </div>
            <button className="btn btn--primary btn--sm" disabled={!!fixing} onClick={republish}>
              {fixing || '앱 호환 형식으로 재발행'}
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="search"><Icon name="search" size={16} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="예제 제목 검색.." /></div>
        <select className="select-el" style={{ width: 'auto' }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">전체 카테고리</option>
          {cats.map(c => <option key={c.id} value={c.id}>{mlGet(c.label, def)}</option>)}
        </select>
        <button className="btn btn--primary" onClick={openNew}><Icon name="plus" size={15} stroke={2.2} />{KIND_LABEL[tab]} 예제 추가</button>
      </div>

      {items === null ? (
        <Loading title="불러오는 중…" sub="공식 코드 예제 목록을 가져오고 있습니다." />
      ) : error ? (
        <ErrorState error={error} title="목록을 불러오지 못했습니다" onRetry={load} />
      ) : shown.length === 0 ? (
        <EmptyState icon={KIND_ICON[tab]} title={`${KIND_LABEL[tab]} 예제가 없습니다`}
          sub={`우측 상단 [${KIND_LABEL[tab]} 예제 추가]로 첫 예제를 등록해보세요.`} />
      ) : (
        <div className="ex-grid">
          {pageItems.map(ex => {
            const c = catById(ex.catId);
            const legacy = isLegacyPythonAsset(ex._meta);
            const hasOrder = ex.orderGroup !== '' && ex.orderIndex !== '';
            return (
              <div key={ex.id} className="ex-card">
                <div className="ex-card__top">
                  <span className={"ex-card__kind ex-card__kind--" + ex.kind}><Icon name={KIND_ICON[ex.kind]} size={13} />{KIND_LABEL[ex.kind]}</span>
                  {hasOrder && <span className="chip mono" style={{ background: 'var(--brand-600)', color: '#fff', fontWeight: 800 }}>{ex.orderGroup}-{ex.orderIndex}</span>}
                  {c && <span className="ex-card__cat"><span className="dot" style={{ background: c.color }}></span>{mlGet(c.label, def)}</span>}
                </div>
                <div className="ex-card__body">
                  <div className="ex-card__title">{mlGet(ex.title, def) || '(제목 없음)'}</div>
                  {mlGet(ex.desc, def) && <div className="ex-card__desc">{mlGet(ex.desc, def)}</div>}
                  {ex.file && <div className="ex-card__file"><Icon name="file" size={14} />{ex.file}</div>}
                  {legacy && (
                    <div className="ex-card__tags" style={{ marginTop: 6 }}>
                      <span className="chip" style={{ background: '#fff4e5', color: '#b4690e' }}>앱 미노출 — 재발행 필요</span>
                    </div>
                  )}
                  {ex.filterIds.length > 0 && (
                    <div className="ex-card__tags">
                      {ex.filterIds.map(fid => { const f = filters.find(x => x.id === fid); return f ? <span key={fid} className="chip">{mlGet(f.label, def)}</span> : null; })}
                    </div>
                  )}
                  {ex.thumbnail && <img src={ex.thumbnail} alt="" onError={e => { e.currentTarget.style.display = 'none'; }}
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginTop: 12 }} />}
                </div>
                <div className="ex-card__foot">
                  <div className="ex-card__langs">
                    {langs.map(l => <span key={l.code} className={"lang-pip" + ((ex.title[l.code] || '').trim() ? " is-set" : "")}>{l.code}</span>)}
                  </div>
                  <button className="icon-btn" onClick={() => download(ex)} title="파일 다운로드"><Icon name="download" size={15} /></button>
                  <button className="icon-btn" onClick={() => openEdit(ex)} title="수정"><Icon name="file" size={15} /></button>
                  <button className="icon-btn icon-btn--danger" onClick={() => setDel(ex)} title="삭제"><Icon name="trash" size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {items !== null && !error && shown.length > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 22 }}>
          <button className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><Icon name="chev-l" size={14} />이전</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button key={n} className={"btn btn--sm" + (n === page ? " btn--primary" : " btn--ghost")} style={{ minWidth: 34 }} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button className="btn btn--ghost btn--sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>다음<Icon name="chev-r" size={14} /></button>
        </div>
      )}

      {editing && draft && (
        <ExampleModal draft={draft} setDraft={setDraft} cats={cats} filters={filters} langs={langs}
          isNew={editing === 'new'} saving={saving} step={step}
          onSave={save} onClose={closeModal} />
      )}
      {del && <Confirm title="예제를 삭제할까요?"
        body={<><b>{mlGet(del.title, def)}</b> 예제를 삭제합니다. 되돌릴 수 없습니다.</>}
        confirmLabel={delBusy ? '삭제 중…' : '삭제'}
        onConfirm={delBusy ? () => {} : remove} onClose={delBusy ? () => {} : () => setDel(null)} />}
    </div>
  );
}

export default ExamplesAdmin;
